import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSession } from '../contexts/SessionContext';
import { useCreditsCheck } from '../hooks/useCreditsCheck';

const DISMISS_KEY = 'lowCreditsBannerDismissed';
const DISMISS_DURATION = 24 * 60 * 60 * 1000;

const WIZARD_ROUTES = [
  '/idea-check',
  '/article-generate',
  '/article-edit',
  '/title-generate',
  '/select-title',
  '/intro-generate',
  '/select-intro',
  '/image-generate',
  '/select-image',
  '/blog-preview',
];

export function LowCreditsBanner() {
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { user: sessionUser } = useSession();
  const user = authUser || sessionUser;

  const creditsInfo = useCreditsCheck(user?.id);
  const [isDismissed, setIsDismissed] = useState(true);

  console.log('[LowCreditsBanner] Debug:', {
    userId: user?.id,
    creditsTotal: creditsInfo.total,
    creditsRegular: creditsInfo.regular,
    creditsTopup: creditsInfo.topup,
    isLoading: creditsInfo.isLoading,
    isDismissed,
    hasUser: !!user
  });

  useEffect(() => {
    const dismissedData = localStorage.getItem(DISMISS_KEY);
    console.log('[LowCreditsBanner] localStorage check:', dismissedData);
    if (dismissedData) {
      const { timestamp } = JSON.parse(dismissedData);
      const now = Date.now();
      const timeSinceDismiss = now - timestamp;
      console.log('[LowCreditsBanner] Dismiss time check:', {
        timestamp,
        now,
        timeSinceDismiss,
        duration: DISMISS_DURATION,
        isStillValid: timeSinceDismiss < DISMISS_DURATION
      });
      if (timeSinceDismiss < DISMISS_DURATION) {
        setIsDismissed(true);
        return;
      }
    }
    console.log('[LowCreditsBanner] Setting isDismissed to FALSE');
    setIsDismissed(false);
  }, []);

  const handleDismiss = () => {
    const dismissData = {
      timestamp: Date.now()
    };
    localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissData));
    setIsDismissed(true);
  };

  const isWizardRoute = WIZARD_ROUTES.some(route => location.pathname.startsWith(route));

  if (isWizardRoute) {
    console.log('[LowCreditsBanner] Not showing: in wizard flow');
    return null;
  }

  if (creditsInfo.isLoading) {
    console.log('[LowCreditsBanner] Not showing: still loading');
    return null;
  }

  if (creditsInfo.total >= 1000) {
    console.log('[LowCreditsBanner] Not showing: credits >= 1000');
    return null;
  }

  if (isDismissed) {
    console.log('[LowCreditsBanner] Not showing: isDismissed is true');
    return null;
  }

  console.log('[LowCreditsBanner] SHOWING BANNER!');


  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-900">
                <span className="font-medium">Je credits zijn bijna op.</span>
                {' '}
                <span className="hidden sm:inline">
                  Je hebt nog <span className="font-semibold">{creditsInfo.total.toLocaleString()}</span> credits.
                </span>
                {' '}
                <a
                  href="/dashboard/settings"
                  className="underline hover:text-amber-700 font-medium"
                >
                  Koop topup credits
                </a>
                {' '}
                <span className="hidden sm:inline">om zonder onderbrekingen door te kunnen werken.</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-md hover:bg-amber-100 transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5 text-amber-600" />
          </button>
        </div>
      </div>
    </div>
  );
}
